import { Component, inject, PLATFORM_ID, REQUEST } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { CookieBannerComponent } from './components/cookie-banner/cookie-banner';
import {
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGS,
  SupportedLang,
  isSupportedLang,
  resolveLanguageFromAcceptLanguage,
  resolveLanguageFromBrowser,
  resolveLanguageFromCookie,
} from './config/language.config';
import { FooterComponent } from './pages/footer/footer.component';
import { HeaderComponent } from './pages/header/header.component';
import { SeoService } from './services/seo/seo.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, HeaderComponent, FooterComponent, CookieBannerComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  private readonly router: Router = inject(Router);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly request = inject(REQUEST, { optional: true });
  // Inject SeoService to eagerly instantiate it — it sets up router event
  // subscriptions in its constructor and is not used by any template directly.
  private readonly seoService = inject(SeoService);

  moduleOnly: boolean = false;

  constructor() {
    this._updateModuleOnly(this.router.url);

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this._updateModuleOnly(event.urlAfterRedirects);
    });

    // Configure language
    this.translate.addLangs([...SUPPORTED_LANGS]);
    this.translate.use(this.resolveInitialLanguage());
  }

  private _updateModuleOnly(url: string) {
    this.moduleOnly = url.startsWith('/osint');
  }

  private resolveInitialLanguage(): SupportedLang {
    if (isPlatformBrowser(this.platformId)) {
      return (
        this.resolveBrowserLanguage() ??
        resolveLanguageFromCookie(document.cookie) ??
        resolveLanguageFromBrowser(this.translate.getBrowserLang()) ??
        'en'
      );
    }

    return (
      resolveLanguageFromCookie(this.request?.headers.get('cookie')) ??
      resolveLanguageFromAcceptLanguage(this.request?.headers.get('accept-language')) ??
      'en'
    );
  }

  private resolveBrowserLanguage(): SupportedLang | null {
    try {
      const storedLang = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      return isSupportedLang(storedLang) ? storedLang : null;
    } catch {
      return null;
    }
  }
}
